/// <amd-module name="Wit/Common/QuerySaveDialog/Components/FolderDropdown" />
import * as React from "react";
import { IReadonlyObservableArray, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { DropdownCallout, DropdownExpandableTextField, IDropdownCalloutProps, IDropdownExpandableProps } from "VSSUI/Dropdown";
import { EditableDropdown, IEditableDropdownProps } from "VSSUI/EditableDropdown";
import { Icon } from "VSSUI/Icon";
import { IListBoxItem, ListBox } from "VSSUI/ListBox";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Tooltip } from "VSSUI/TooltipEx";
import { ExpandableTreeCell, ITreeColumn } from "VSSUI/TreeEx";
import { css } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import * as Resources from "Wit/Common/QuerySaveDialog/Resources";
import { EmptyItem, QueryItem } from "Wit/Common/QueryUtils/Models";

export interface IFolderTreeDropdownProps
    extends Pick<
        IEditableDropdownProps<QueryItem | EmptyItem>,
        "allowClear" | "allowFreeform" | "className" | "disabled" | "onValueChange" | "selectedText" | "text"
    > {
    /**
     * List of query items wrapped in list box items
     */
    items: IReadonlyObservableArray<IListBoxItem<QueryItem | EmptyItem>>;
    /**
     * Callback when the path changes
     */
    onSetFolder?: (newFolder: string) => void;
    /**
     * Callback for on toggle
     */
    onToggled?: (event: React.SyntheticEvent<HTMLElement, Event>, item: IListBoxItem<QueryItem | EmptyItem>) => void;
}

/**
 * Dropdown that shows a tree of query folders
 */
export const FolderDropdown: React.FunctionComponent<IFolderTreeDropdownProps> = props => {
    const { items, onSetFolder, onToggled, onValueChange, selectedText, disabled, ...rest } = props;
    const columns = defaultColumns;
    const folderTreeItemProvider = React.useRef(items);
    const expandedMap = React.useRef(new Map<string, boolean>());
    const selection = React.useRef(new DropdownSelection());
    const listBoxRef = React.useRef<React.RefObject<ListBox<{}>>>();

    const renderCallout = (props: IDropdownCalloutProps<QueryItem | EmptyItem>) => {
        listBoxRef.current = props.listBoxRef;
        return <DropdownCallout {...props} />;
    };

    const renderExpandable = (props: IDropdownExpandableProps<QueryItem | EmptyItem>) => {
        const updatedProps = {
            autoSelect: true,
            ...props
        };

        return <DropdownExpandableTextField {...updatedProps} />;
    };

    const onExpand = () => {
        // delay for a moment so the listbox can render
        setTimeout(() => {
            if (listBoxRef.current?.current && selection.current.value.length) {
                listBoxRef.current?.current.scrollIntoView(selection.current.value[0].beginIndex, {
                    block: "nearest"
                });
            }
        }, 0);
    };

    const updateSelection = (selectedText: string) => {
        const expandedItems = folderTreeItemProvider.current.value.filter(v => !v.parent || v.parent.expanded);
        const index = expandedItems.findIndex(item => item.text === selectedText);
        if (index > -1) {
            selection.current.select(index);
        }
    };

    const onSelectedValueChanged = () => {
        if (!selectedText) {
            return;
        }

        // find the selected list box item
        const text = ObservableLike.getValue(selectedText);
        const selectedItem = items.value.find(item => !item.data?.isEmptyFolderContext && item.data!.path === text);

        // iterate through and expand all of the selected item's ancestors
        let current = selectedItem?.parent;
        while (current) {
            current.expanded = true;
            expandedMap.current.set(current.id, true);
            current = current.parent;
        }

        updateSelection(text);
    };

    const onItemsChange = () => {
        // update list box items expanded state based on internal toggled map.
        for (const item of items.value) {
            const expanded = expandedMap.current.get(item.id);
            if (expanded !== undefined) {
                item.expanded = expanded;
            }
        }

        onSelectedValueChanged();
    };

    React.useEffect(() => {
        const itemsCallback = items.subscribe(onItemsChange);
        const selectedTextCallback = ObservableLike.isObservable(selectedText) && selectedText.subscribe(onSelectedValueChanged);

        return () => {
            items.unsubscribe(itemsCallback);
            if (selectedTextCallback && ObservableLike.isObservable(selectedText)) {
                selectedText.unsubscribe(selectedTextCallback);
            }
        };
    }, [items, selectedText]);

    // account for selected text being passed as a string
    React.useEffect(onSelectedValueChanged, [selectedText]);

    return (
        <EditableDropdown
            allowTextSelection
            columns={columns}
            items={folderTreeItemProvider.current}
            filterItems={() => {
                return {
                    filteredItems: folderTreeItemProvider.current.value,
                    filteredIndexMap: folderTreeItemProvider.current.value.map((item, index) => index),
                    filterMatches: []
                };
            }}
            onExpand={onExpand}
            onToggle={(e, item) => {
                // update toggled status of the item
                item.expanded = !item.expanded;
                expandedMap.current.set(item.id, item.expanded);

                // update the selection after toggling rows into the view
                if (selectedText) {
                    const text = ObservableLike.getValue(selectedText);
                    updateSelection(text);
                }

                onToggled && onToggled(e, item);
            }}
            onValueChange={value => {
                if (onSetFolder) {
                    const nodePath = (value?.data as QueryItem)?.path ?? "";
                    onSetFolder(nodePath);
                }

                if (onValueChange) {
                    onValueChange(value);
                }
            }}
            renderCallout={renderCallout}
            renderExpandable={renderExpandable}
            selection={selection.current}
            selectedText={selectedText}
            disabled={disabled}
            showTree
            {...rest}
        />
    );
};

const defaultColumns: ITreeColumn<IListBoxItem<QueryItem | EmptyItem>>[] = [
    {
        id: "title",
        width: new ObservableValue(-100),
        renderCell: renderItemColumn
    }
];

function renderItemColumn(
    rowIndex: number,
    columnIndex: number,
    treeColumn: ITreeColumn<IListBoxItem<QueryItem | EmptyItem>>,
    treeItem: ITreeItemEx<IListBoxItem<QueryItem | EmptyItem>>
): JSX.Element {
    const item = treeItem.underlyingItem.data;

    let content: JSX.Element;

    if (item.data?.isLoadingContext) {
        content = <Spinner size={SpinnerSize.medium} />;
    } else if (item.data?.isEmptyFolderContext) {
        content = (
            <span className="bolt-list-cell-child flex-row flex-center bolt-list-cell-text">
                <span className="secondary-text text-ellipsis">{Resources.EmptyFolder}&nbsp;</span>
            </span>
        );
    } else {
        const text = item.data?.name;
        content = (
            <div className="flex-row flex-grow queries-hub-new-folder-dropdown ">
                <span className="bolt-list-cell-child flex-row flex-center bolt-list-cell-text">
                    <Icon className={css("margin-right-8", "queries-folder-icon")} iconName={"FabricFolderFill"} />
                    <Tooltip overflowOnly={true}>
                        <span className="inline-flex-row scroll-hidden">
                            <span className="text-ellipsis">{text}</span>
                        </span>
                    </Tooltip>
                </span>
            </div>
        );
    }

    return (
        <ExpandableTreeCell
            className={css(treeItem.className, treeColumn.className)}
            columnIndex={columnIndex}
            key={`title-cell-${rowIndex}-${columnIndex}`}
            treeColumn={treeColumn}
            treeItem={treeItem}
        >
            {content}
        </ExpandableTreeCell>
    );
}
